// src/pages/NewStudyPage.js - 새로운 리서치 생성 페이지 (폼 + 챗봇)

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faSpinner,
  faPaperPlane,
  faCopy,
  faCheck,
  faMinimize,
  faWindowMaximize,
} from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import WorkspaceLayout from "../components/WorkspaceLayout";
import B2BLayout from "../components/b2b/B2BLayout";
import { AlertModal, Button, Icon24, Toast } from "../components/ui";
import UpgradeModal from "../components/UpgradeModal";
import sparkleIcon from "../assets/icons/special/ico_sparkle.svg";
import "./NewStudyPage.scss";
import { generateStudyName, generateProjectName } from "../services/api";
import { apiJson, apiStream } from "../services/apiClient";
import { usePageHeader } from "../context/PageHeaderContext";
import { useB2BMode } from "../hooks/useB2BMode";

const NewStudyPage = () => {
  const { projectSlug, projectId: projectIdParam } = useParams();
  const navigate = useNavigate();
  const {
    user,
    isLoggedIn,
    sessionChecked,
    getProject,
    loadProjects,
    workspaceLoaded,
    createProject: createProjectInContext,
    canCreateStudy,
    canCreateProject,
  } = useAppContext();
  const { isB2BMode } = useB2BMode();
  const Layout = isB2BMode ? B2BLayout : WorkspaceLayout;
  const projectKey = projectSlug || projectIdParam;
  const project = getProject(projectKey);
  const projectId = project?.id ?? projectIdParam ?? null;

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn) return;
    loadProjects();
  }, [isLoggedIn, loadProjects, sessionChecked]);

  usePageHeader(
    {
      title: project ? "새 리서치 생성" : "새 리서치 생성",
      subtitle: project?.name || "",
      showBack: true,
      onBack: () => navigate(-1),
    },
    [project]
  );

  // === 폼 데이터 상태 ===
  const [formData, setFormData] = useState({
    studyName: "",
    problemDefinition: "", // 필수
    methodologies: [], // 선택적
    timeline: "", // 선택적
    startDate: "", // 시작일 (선택적)
    endDate: "", // 예상 종료일 (선택적) - 새로 추가
    budget: "", // 선택적
    targetAudience: "", // 선택적
    participantCount: "", // 인원수 (선택적)
    additionalRequirements: "", // 선택적
  });

  // === 카드 단계 상태 ===
  // 리서치명은 자동 생성되므로 문제 정의부터 시작
  const [currentStep, setCurrentStep] = useState(2);

  // === 챗봇 상태 ===
  const [chatMessages, setChatMessages] = useState([
    {
      type: "assistant",
      content: "안녕하세요! \n궁금한 점이 있으시면 언제든 물어보세요!",
    },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false); // 계획서 생성 중복 클릭 방지
  const isGeneratingRef = useRef(false);
  const activeRequestIdRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [customMethodology, setCustomMethodology] = useState("");
  const [isCustomMethodologyComposing, setIsCustomMethodologyComposing] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedSet, setExpandedSet] = useState(new Set());
  const isExpanded = (i) => expandedSet.has(i);
  const toggleExpand = (i) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  // Alert 모달 상태
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'success', // 'success' or 'error'
    message: ''
  });

  // Upgrade 모달 상태
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    reason: 'study'
  });

  // Toast 상태
  const [toast, setToast] = useState({
    isOpen: false,
    type: 'success',
    message: ''
  });

  // Alert 표시 함수
  const showAlert = (type, message) => {
    setAlertModal({
      isOpen: true,
      type,
      message
    });
  };

  // === 방법론 옵션 ===
  const methodologyOptions = [
    "심층 인터뷰",
    "사용성 테스트",
    "포커스 그룹",
    "관찰 조사",
    "카드 소팅",
    "다이어리 스터디",
  ];

  // === 카드 refs ===
  const studyNameCardRef = useRef(null);
  const problemDefCardRef = useRef(null);
  const problemDefTextareaRef = useRef(null);
  const additionalInfoCardRef = useRef(null);
  const formSectionRef = useRef(null);

  // workspace-body 바깥 스크롤 잠금 — 좌측 폼/우측 채팅 내부에서만 스크롤
  useEffect(() => {
    const bodyEl = document?.querySelector?.(".workspace-body, .b2b-body");
    const layoutEl = document?.querySelector?.(".workspace-layout, .b2b-layout");
    const contentEl = document?.querySelector?.(".workspace-content, .b2b-content");
    if (!bodyEl) return undefined;

    layoutEl?.classList?.add("workspace-layout--scroll-locked");
    layoutEl?.classList?.add("b2b-layout--scroll-locked");
    contentEl?.classList?.add("workspace-content--scroll-locked");
    contentEl?.classList?.add("b2b-content--scroll-locked");
    bodyEl.classList.add("workspace-body--scroll-locked");
    bodyEl.classList.add("b2b-body--scroll-locked");

    return () => {
      bodyEl.classList.remove("workspace-body--scroll-locked");
      bodyEl.classList.remove("b2b-body--scroll-locked");
      layoutEl?.classList?.remove("workspace-layout--scroll-locked");
      layoutEl?.classList?.remove("b2b-layout--scroll-locked");
      contentEl?.classList?.remove("workspace-content--scroll-locked");
      contentEl?.classList?.remove("b2b-content--scroll-locked");
    };
  }, []);

  // === Tier 제한 체크 ===
  useEffect(() => {
    // 새로고침/초기 마운트 직후에는 AppContext가 localStorage에서 user를 복원하기 전이라
    // canCreateStudy()가 false를 반환할 수 있다. (그 결과 premium 계정도 Free 제한 팝업이 뜸)
    // → 인증/등급 정보가 확정된 이후에만 제한 체크를 수행한다.
    if (!isLoggedIn || !workspaceLoaded) return;
    if (!user || !user.tier) return;

    // projectId가 없어도 체크 (Free 사용자는 전체 개수로 체크되므로)
    if (!canCreateStudy(projectId || 'temp')) {
      setUpgradeModal({ isOpen: true, reason: 'study' });
      // 사용자가 닫기 버튼을 누를 때까지 팝업 유지
    }
  }, [isLoggedIn, user?.tier, projectId, workspaceLoaded, canCreateStudy]);

  // === 챗봇 스크롤 (채팅 패널 내부만 — 폼 영역 scrollIntoView 방지) ===
  useEffect(() => {
    const chatArea = chatMessagesRef.current;
    if (!chatArea) return;
    chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: "smooth" });
  }, [chatMessages]);

  // === 카드 스크롤 (폼 영역 내부만) ===
  useEffect(() => {
    const formArea = formSectionRef.current;
    const target =
      currentStep === 2
        ? problemDefCardRef.current
        : currentStep === 3
          ? additionalInfoCardRef.current
          : null;
    if (!formArea || !target) return undefined;

    const timer = setTimeout(() => {
      const formRect = formArea.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop =
        targetRect.top -
        formRect.top +
        formArea.scrollTop -
        formArea.clientHeight / 2 +
        targetRect.height / 2;
      formArea.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
    }, 300);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // === 문제 정의 textarea 높이 자동 조정 ===
  useEffect(() => {
    if (problemDefTextareaRef.current) {
      problemDefTextareaRef.current.style.height = 'auto';
      problemDefTextareaRef.current.style.height = `${Math.max(problemDefTextareaRef.current.scrollHeight, 44)}px`; // 최소 한 줄 높이
    }
  }, [formData.problemDefinition]);

  const autoResizeElement = useCallback((element) => {
    if (element) {
      element.style.height = 'auto';
      const newHeight = Math.max(element.scrollHeight, 40);
      element.style.height = `${newHeight}px`;
    }
  }, []);

  const handleAutoResize = useCallback(
    (event) => {
      autoResizeElement(event.currentTarget);
    },
    [autoResizeElement]
  );

  // 방법론 직접입력함수
  const handleAddCustomMethodology = (method) => {
    const trimmed = method.trim();
    if (!trimmed) return;
    if (!formData.methodologies.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        methodologies: [...prev.methodologies, trimmed]
      }));
    }
  };

  // === 폼 입력 핸들러 ===
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeyPress = (e, nextStep) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      setCurrentStep(nextStep);
    }
  };

  // === AI 도움 버튼 핸들러 ===
  const handleAIHelp = async (question, category = "general") => {
    if (isChatLoading) return;
    // 챗봇이 최소화되어 있다면 확장
    if (isChatMinimized) {
      setIsChatMinimized(false);
    }
    // 바로 챗봇에 질문 전송
    setChatMessages((prev) => [...prev, { type: "user", content: question }]);
    setIsChatLoading(true);

    try {
      const contextInfo = {
        currentForm: formData,
        projectName: project?.name || "프로젝트",
        category: category, // 카테고리 정보 추가
      };

      const response = await apiStream("/study-helper/chat", {
        method: "POST",
        body: JSON.stringify({
          message: question,
          context: contextInfo,
          mode: 'help',
          task: category,
        }),
      });

      if (!response.ok) throw new Error("챗봇 응답 실패");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";

      setChatMessages((prev) => [...prev, { type: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.content) {
                assistantMessage += data.content;
                setChatMessages((prev) => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1].content =
                    assistantMessage;
                  return newMessages;
                });
              }
            } catch (e) {
              // JSON 파싱 에러 무시
            }
          }
        }
      }
    } catch (error) {
      console.error("챗봇 오류:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content: "죄송합니다. 일시적인 오류가 발생했습니다.",
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleMethodologyToggle = (methodology) => {
    setFormData((prev) => ({
      ...prev,
      methodologies: prev.methodologies.includes(methodology)
        ? prev.methodologies.filter((m) => m !== methodology)
        : [...prev.methodologies, methodology],
    }));
  };

  // === 챗봇 메시지 전송 ===
  const sendChatMessage = async (opts = {}) => {
    const { mode = 'general', task = null, overrideMessage = null } = opts;
    if (isChatLoading) return;
    const raw = overrideMessage ?? chatInput;
    if (!raw || !raw.trim()) return;

    const userMessage = raw.trim();
    setChatInput("");

    // 사용자 메시지 추가
    setChatMessages((prev) => [
      ...prev,
      { type: "user", content: userMessage },
    ]);
    setIsChatLoading(true);

    try {
      // 현재 폼 데이터를 컨텍스트로 포함
      const contextInfo = {
        currentForm: formData,
        projectName: project?.name || "프로젝트",
      };

      const response = await apiStream("/study-helper/chat", {
        method: "POST",
        body: JSON.stringify({
          message: userMessage,
          context: contextInfo,
          mode,
          task,
        }),
      });

      if (!response.ok) throw new Error("챗봇 응답 실패");

      // 스트리밍 응답 처리
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";

      // 빈 어시스턴트 메시지 추가
      setChatMessages((prev) => [...prev, { type: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.content) {
                assistantMessage += data.content;
                // 실시간으로 메시지 업데이트
                setChatMessages((prev) => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1].content =
                    assistantMessage;
                  return newMessages;
                });
              }
            } catch (e) {
              // JSON 파싱 에러 무시
            }
          }
        }
      }
    } catch (error) {
      console.error("챗봇 오류:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content:
            "죄송합니다. 일시적인 오류가 발생했습니다. 다시 시도해주세요.",
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // === 계획서 생성 시작 ===
  const handleGeneratePlan = async () => {
    if (isGeneratingRef.current) return;

    isGeneratingRef.current = true;
    setIsGenerating(true); // 로딩 시작
    try {
      if (!workspaceLoaded) {
        const loadedProjects = await loadProjects();
        if (!Array.isArray(loadedProjects)) {
          showAlert("error", "프로젝트 정보를 불러오지 못했습니다. 다시 시도해주세요.");
          return;
        }
      }

      const currentProject = getProject(projectKey);
      let targetProjectId = currentProject?.id ?? projectIdParam ?? null;

      // Tier 체크: 현재 프로젝트의 스터디 수와 프로젝트 수를 확인합니다.
      if (!canCreateStudy(targetProjectId || "temp")) {
        setUpgradeModal({ isOpen: true, reason: "study" });
        return;
      }

      if (!formData.problemDefinition.trim()) {
        showAlert("error", "문제 정의는 필수 입력 항목입니다.");
        return;
      }

      if (formData.problemDefinition.trim().length < 10) {
        showAlert("error", "최소 10자 이상 입력해주세요.");
        return;
      }

      let finalStudyName = formData.studyName.trim() || ""; // [수정] 빈 문자열 허용

      // projectId가 없을 때: 프로젝트 먼저 생성
      if (!targetProjectId) {
        // ⭐ Tier 체크: 프로젝트 생성 가능 여부 확인
        if (!canCreateProject()) {
          setUpgradeModal({ isOpen: true, reason: 'project' });
          return;
        }
        
        // [수정] 리서치명이 없으면 일단 임시로 넣고 진행
        const projectNameForGeneration = finalStudyName || "새 리서치";
        
        // 1. 프로젝트명 생성
        const projectNameResult = await generateProjectName(
          projectNameForGeneration,
          formData.problemDefinition
        );

        if (!projectNameResult.success) {
          showAlert("error", "프로젝트명 생성에 실패했습니다.");
          return;
        }

        const autoProjectName = projectNameResult.projectName;
        const autoTags = projectNameResult.tags || []; // 태그 추가

        // 2. 프로젝트 생성
        const projectData = {
          name: autoProjectName,
          domain: "general",
          tags: autoTags, // 생성된 태그 사용
        };

        const createdProject = await createProjectInContext(projectData);
        
        if (createdProject && createdProject.id) {
          targetProjectId = createdProject.id;
          // AppContext에서 이미 목록에 추가됨
        } else {
          showAlert("error", "프로젝트 생성에 실패했습니다.");
          return;
        }
      }

      // 3. 리서치명이 없으면 먼저 생성
      if (!finalStudyName) {
        try {
          const studyNameResult = await generateStudyName(formData.problemDefinition);
          if (studyNameResult.success && studyNameResult.studyName) {
            finalStudyName = studyNameResult.studyName;
          } else {
            // 실패하면 임시 이름 사용
            finalStudyName = `리서치-${Date.now()}`;
          }
        } catch (error) {
          console.error("리서치명 생성 오류:", error);
          // 실패하면 임시 이름 사용
          finalStudyName = `리서치-${Date.now()}`;
        }
      }

      // 4. 리서치 생성 (이제 이름이 확정된 상태)
      const ensureRequestId = () => {
        if (activeRequestIdRef.current) return activeRequestIdRef.current;
        const cryptoApi =
          typeof window !== "undefined" &&
          window.crypto &&
          typeof window.crypto.randomUUID === "function"
            ? window.crypto
            : undefined;
        if (cryptoApi) {
          activeRequestIdRef.current = cryptoApi.randomUUID();
        } else {
          activeRequestIdRef.current = `req-${Date.now()}-${Math.random()
            .toString(16)
            .slice(2)}`;
        }
        return activeRequestIdRef.current;
      };

      const requestId = ensureRequestId();
      
      const result = await apiJson("/generator/create-plan-oneshot", {
        method: "POST",
        body: JSON.stringify({
          formData: { ...formData, studyName: finalStudyName },
          projectId: targetProjectId,
          requestId,
        }),
      });

      if (result.success && result.study_id && result.artifact_id) {
        activeRequestIdRef.current = null;
        // ⭐ StudyWorkflowPage로 이동 (slug 사용)
        const studySlug = result.study_slug || result.study_id; // slug 우선, 없으면 ID 사용
        navigate(`/study/${studySlug}`);
        // [TODO: 나중에 다시 살릴 코드] 계획서 artifact 페이지로 직접 이동
        // navigate(`/study/${studySlug}/artifact/${result.artifact_id}`);
      } else {
        showAlert(
          "error",
          "리서치 생성에 실패했습니다: " + (result.error || "알 수 없는 오류")
        );
      }
    } catch (error) {
      console.error("생성 오류:", error);
      showAlert("error", "리서치 생성 중 오류가 발생했습니다.");
    } finally {
      setIsGenerating(false); // 로딩 끝
      isGeneratingRef.current = false;
    }
  };

  // === 버튼 활성화 조건 ===
  // 문제 정의만 있으면 활성화 (리서치명은 자동 생성됨)
  // 로딩 중에는 비활성화 (중복 클릭 방지)
  const isGenerateButtonEnabled = formData.problemDefinition.trim() && !isGenerating;

  // projectId가 없을 때는 리서치 생성 페이지를 표시하되, 프로젝트는 나중에 생성

  return (
    <Layout hideCollapseButton={false} defaultCollapsed={true}>
      <div className="new-study-container">
        {/* 헤더 */}
        {/* 메인 콘텐츠 */}
        <div className="new-study-content">
          {/* 좌측: 카드 영역 */}
          <div className="form-section" ref={formSectionRef}>
            <div className="cards-container">
              {/* 타이틀 헤더 */}
              <div className="welcome-section">
                <div className="welcome-header">
                  <h2>리서치 목표와 배경을 알려주세요</h2>
                </div>
              </div>

              {/* 카드 1: 리서치 제목 (리서치명은 자동 생성되므로 숨김) */}
              {false && currentStep >= 1 && (
                <div className="input-card" ref={studyNameCardRef}>
                  <div className="card-header">
                    <h3>생성할 연구의 제목을 입력해주세요</h3>
                  </div>
                  <div className="card-content">
                    <div className="input-with-button">
                      <input
                        type="text"
                        className="card-input"
                        placeholder="예: 신규 기능 사용성 개선 연구"
                        value={formData.studyName}
                        onChange={(e) =>
                          handleInputChange("studyName", e.target.value)
                        }
                        onKeyPress={(e) => handleKeyPress(e, 2)}
                        autoFocus
                      />
                      <button
                        className="input-submit-btn"
                        onClick={() =>
                          formData.studyName.trim() && setCurrentStep(2)
                        }
                        disabled={!formData.studyName.trim()}
                      >
                        ↵
                      </button>
                    </div>
                    <div className="card-help">
                      <button
                        className="example-btn"
                        onClick={() =>
                          handleAIHelp("연구 제목 예시를 몇 개 보여줘")
                        }
                      >
                        💡 좋은 제목 예시 보기
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 카드 2: 문제 정의 */}
              {currentStep >= 2 && (
                <div
                  className="input-card"
                  style={{ animationDelay: "0.2s" }}
                  ref={problemDefCardRef}
                >
                  <div className="card-header">
                    <div className="form-label-row">
                      <h3>
                        조사를 통해 알고 싶거나 해결하고 싶은 문제는 무엇인가요?
                      </h3>
                      <button
                        className="ai-suggest-btn"
                        onClick={() =>
                          handleAIHelp(
                            "좋은 문제 정의 예시를 보여줘",
                            "problem_definition"
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faRobot} />
                        AI 추천받기
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <textarea
                      ref={problemDefTextareaRef}
                      className="card-textarea"
                      placeholder="여기에 알고 싶거나 해결하고 싶은 문제 입력해주세요."
                      rows={1}
                      value={formData.problemDefinition}
                      onChange={(e) => {
                        handleInputChange("problemDefinition", e.target.value);
                      }}
                      autoFocus={currentStep === 2}
                      style={{
                        resize: 'none',
                        overflow: 'hidden',
                        minHeight: '44px',
                      }}
                    />

                    {/* 선택 안내 */}
                    {formData.problemDefinition.trim() && (
                      <div className="next-step-hint">
                        <div className="hint-content">
                          <div className="hint-text">
                            <span className="hint-check">✓</span>
                            <div>
                              <strong>
                              잠깐! 생성 전에 이런 점을 구체화 해보면 좋아요 
                              </strong>
                            </div>
                          </div>
                          <button
                            className="optional-input-btn"
                            onClick={() => setCurrentStep(3)}
                          >
                            더 입력하기
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 카드 3: 추가 정보 (선택) */}
              {currentStep >= 3 && (
                <div
                  className="input-card"
                  style={{ animationDelay: "0.3s" }}
                  ref={additionalInfoCardRef}
                >
                  <div className="card-header">
                    <h3>추가 정보 (선택)</h3>
                    <p>
                    상세 정보를 추가로 입력하시면 AI가 계획서를 더욱 정교하게 만들어요
                    </p>
                  </div>
                  <div className="card-content">
                    {/* 연구 방법론 선택 */}
                    <div className="form-group">
                      <div className="form-label-row">
                        <label className="form-label">어떤 방법으로 조사하고자 하시나요?</label>
                        <button
                          className="ai-suggest-btn"
                          onClick={() =>
                            handleAIHelp(
                              "이 연구에 적합한 조사 방법을 추천해줘",
                              "methodology"
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faRobot} />
                          AI 추천받기
                        </button>
                      </div>
                      <div className="methodology-tags">
                        {methodologyOptions.map((method) => (
                          <button
                            key={method}
                            className={`method-tag ${
                              formData.methodologies.includes(method)
                                ? "active"
                                : ""
                            }`}
                            onClick={() => handleMethodologyToggle(method)}
                          >
                            {method}
                          </button>
                        ))}

                        {formData.methodologies
                          .filter((method) => !methodologyOptions.includes(method))
                          .map((method) => (
                            <button
                              key={method}
                              className={`method-tag ${
                                formData.methodologies.includes(method)
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => handleMethodologyToggle(method)}
                            >
                              {method}
                            </button>
                          ))}

                        {/* 직접 입력 방법론 추가 */}
                        {!showCustomInput ? (
                          <button
                            className="method-tag custom-input-tag"
                            onClick={() => setShowCustomInput(true)}
                          >
                            직접 입력
                          </button>
                        ) : (
                          <div className="custom-input-container">
                            <input
                            type="text"
                            className="method-tag-input"
                            placeholder="수행하고자 하는 방법을 입력 후 Enter"
                            value={customMethodology}
                            onChange={(e) => {
                              setCustomMethodology(e.target.value);
                            }}
                            onCompositionStart={() => setIsCustomMethodologyComposing(true)}
                            onCompositionEnd={(e) => {
                              setIsCustomMethodologyComposing(false);
                              setCustomMethodology(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !isCustomMethodologyComposing) {
                                e.preventDefault();
                                const trimmed = customMethodology.trim();
                                if (trimmed) {
                                  handleAddCustomMethodology(trimmed);
                                  setCustomMethodology("");
                                }
                              } else if (e.key === "Escape" && !isCustomMethodologyComposing) {
                                setCustomMethodology("");
                                setShowCustomInput(false);
                              }
                            }}
                            onBlur={() => {
                              const trimmed = customMethodology.trim();
                              if (trimmed) {
                                handleAddCustomMethodology(trimmed);
                              }
                              setCustomMethodology("");
                              setShowCustomInput(false);
                            }}
                            autoFocus
                          />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 조사 대상 + 인원수 */}
                    <div className="form-group">
                      <div className="form-label-row">
                        <label className="form-label">
                          조사를 통해 만나고자 하는 대상은 누구인가요?
                        </label>
                        <button
                          className="ai-suggest-btn"
                          onClick={() =>
                            handleAIHelp(
                              "이 연구에 적합한 조사 대상자를 추천해줘",
                              "target"
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faRobot} />
                          AI 추천받기
                        </button>
                      </div>
                      <div className="form-row-with-labels">
                        <div className="form-col">
                          <label className="form-sublabel">
                            조사 규모
                          </label>
                          <div className="input-with-unit">
                            <input
                              type="number"
                              className="card-input"
                              value={formData.participantCount}
                              onChange={(e) =>
                                handleInputChange(
                                  "participantCount",
                                  e.target.value
                                )
                              }
                            />
                            <span className="input-unit">명</span>
                          </div>
                        </div>
                        <div className="form-col wide">
                          <label className="form-sublabel">대상의 구분</label>
                          <textarea
                            className="card-input auto-textarea"
                            placeholder="예: 필수) 최근 3개월 이내 신규 가입유저 , 그룹 구분) 앱 이용 빈도, 거래 규모"
                            value={formData.targetAudience}
                            rows={1}
                            ref={autoResizeElement}
                            onChange={(e) =>
                              handleInputChange(
                                "targetAudience",
                                e.target.value
                              )
                            }
                            onInput={handleAutoResize}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 희망 일정 */}
                    <div className="form-group">
                      <div className="form-label-row">
                        <label className="form-label">
                          조사의 일정은 어떻게 되나요?
                        </label>
                        <button
                          className="ai-suggest-btn"
                          onClick={() =>
                            handleAIHelp(
                              "이 연구에 적합한 일정을 추천해줘",
                              "timeline"
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faRobot} />
                          AI 추천받기
                        </button>
                      </div>
                      <div className="form-row-with-labels date-row">
                        <div className="form-col">
                          <label className="form-sublabel">
                            시작 일정
                          </label>
                          <input
                            type="date"
                            className="card-input"
                            value={formData.startDate}
                            onChange={(e) =>
                              handleInputChange("startDate", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-col">
                          <label className="form-sublabel">
                            종료 일정
                          </label>
                          <input
                            type="date"
                            className="card-input"
                            value={formData.endDate}
                            onChange={(e) =>
                              handleInputChange("endDate", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-col">
                          <label className="form-sublabel">조사의 기간</label>
                          <textarea
                            className="card-input auto-textarea"
                            placeholder="예: 2주, 1달 등"
                            value={formData.timeline}
                            rows={1}
                            ref={autoResizeElement}
                            onChange={(e) =>
                              handleInputChange("timeline", e.target.value)
                            }
                            onInput={handleAutoResize}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 예상 예산 - 주석 처리 (계획서에 포함하지 않음) */}
                    {/* <div className="form-group">
                      <div className="form-label-row">
                        <label className="form-label">정해진 예산은 어느정도인가요?</label>
                        <button
                          className="ai-suggest-btn"
                          onClick={() =>
                            handleAIHelp(
                              "이 연구에 적합한 예산 규모를 추천해줘",
                              "budget"
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faRobot} />
                          AI 추천받기
                        </button>
                      </div>
                      <input
                        type="text"
                        className="card-input"
                        placeholder="예: 500만원"
                        value={formData.budget}
                        onChange={(e) =>
                          handleInputChange("budget", e.target.value)
                        }
                      />
                    </div> */}
                  </div>

                  {/* 기타 요구사항 */}
                  <div className="form-group">
                    <div className="form-label-row">
                      <label className="form-label">기타 요구사항 (선택)</label>
                      <button
                        className="ai-suggest-btn"
                        onClick={() =>
                          handleAIHelp(
                            "이 연구에 추가로 고려해야 할 요구사항을 추천해줘",
                            "additional"
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faRobot} />
                        AI 추천받기
                      </button>
                    </div>
                    <textarea
                      className="card-textarea auto-textarea"
                      placeholder="예: 특정 도구 사용, 특별한 분석 방법, 제약사항 등"
                      rows={1}
                      ref={autoResizeElement}
                      value={formData.additionalRequirements}
                      onChange={(e) =>
                        handleInputChange("additionalRequirements", e.target.value)
                      }
                      onInput={handleAutoResize}
                    />
                  </div>




                </div>
              )}
            </div>

            {/* 플로팅 계획서 생성 버튼 */}
            <div className={`floating-action ${isChatMinimized ? 'full' : ''}`}>
              <Button
                variant="primary"
                size="lg"
                onClick={handleGeneratePlan}
                disabled={!isGenerateButtonEnabled}
                loading={isGenerating}
                loadingText="생성 중..."
                leftIcon={(
                  <img src={sparkleIcon} alt="" aria-hidden="true" width="24" height="24" />
                )}
              >
                계획서 만들기
              </Button>
            </div>
          </div>

          {/* 우측: 챗봇 영역 */}
          {!isChatMinimized && (
          <div className={`chat-section`}>
            <div className="chat-container">
              <div 
                className="chat-header"
                onClick={() => false}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FontAwesomeIcon icon={faRobot} />
                  <span>리서치 설계 도우미</span>
                </div>
                <button 
                  className="chat-minimize-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsChatMinimized(true);
                  }}
                  title={'최소화'}
                >
                  <Icon24 name="floating" />
                </button>
              </div>

              <div className="chat-messages" ref={chatMessagesRef}>
                {chatMessages.map((message, index) => (
                  <div key={index} className={`chat-message ${message.type}`}>
                    <div className="message-content" style={{ position: 'relative', marginBottom: 10 }}>
                      {message.type === "assistant" ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ children }) => (
                              <h1 className="markdown-h1">{children}</h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="markdown-h2">{children}</h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="markdown-h3">{children}</h3>
                            ),
                            p: ({ children }) => (
                              <p className="markdown-p">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="markdown-ul">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="markdown-ol">{children}</ol>
                            ),
                            li: ({ children }) => (
                              <li className="markdown-li">{children}</li>
                            ),
                            strong: ({ children }) => (
                              <strong className="markdown-strong">
                                {children}
                              </strong>
                            ),
                            em: ({ children }) => (
                              <em className="markdown-em">{children}</em>
                            ),
                            code: ({ children }) => (
                              <code className="markdown-code">{children}</code>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="markdown-blockquote">
                                {children}
                              </blockquote>
                            ),
                            // 취소선 비활성화 (~~텍스트~~가 의도치 않게 적용되는 것 방지)
                            del: ({ children }) => <>{children}</>,
                          }}
                        >
                          {(() => {
                            const full = message.content || '';
                            const limit = 900; // UI 기본 표시 제한
                            if (isExpanded(index) || full.length <= limit) return full;
                            return full.slice(0, limit) + '...';
                          })()}
                        </ReactMarkdown>
                      ) : (
                        message.content
                      )}
                      {message.type === 'assistant' && message.content && message.content.length > 900 && (
                        <button
                          onClick={() => toggleExpand(index)}
                          style={{
                            position: 'absolute',
                            left: 6,
                            bottom: 6,
                            border: 'none',
                            background: 'transparent',
                            color: '#2563eb',
                            cursor: 'pointer',
                            fontSize: 12,
                            padding: 4,
                          }}
                        >
                          {isExpanded(index) ? '접기' : '더보기'}
                        </button>
                      )}
                      {message.type === 'assistant' && index !== 0 && (message.content && message.content.trim()) && (
                        <button
                          aria-label="복사"
                          title={copiedIndex === index ? '복사됨' : '복사'}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(message.content || '');
                              setCopiedIndex(index);
                              setToast({
                                isOpen: true,
                                type: 'success',
                                message: '클립보드에 복사되었습니다!'
                              });
                              setTimeout(() => setCopiedIndex(null), 1200);
                            } catch (e) {
                              setToast({
                                isOpen: true,
                                type: 'error',
                                message: '복사에 실패했습니다'
                              });
                            }
                          }}
                          className="chat-copy-btn"
                        >
                          <FontAwesomeIcon icon={copiedIndex === index ? faCheck : faCopy} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="chat-message assistant">
                    <div className="message-content">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>답변 생성 중...</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="chat-input-container">
                <input
                  type="text"
                  className="chat-input"
                  placeholder="궁금한 점을 물어보세요..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendChatMessage()}
                  disabled={isChatLoading}
                />
                <button
                  className="chat-send-btn"
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || isChatLoading}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            </div>
          </div>
          )}

          {/* 최소화된 챗봇 작은 창 */}
          {isChatMinimized && (
            <div className="chat-minimized-window">
              <div 
                className="chat-minimized-header"
                onClick={() => setIsChatMinimized(false)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FontAwesomeIcon icon={faRobot} />
                  <span>리서치 설계 도우미</span>
                </div>
                <button 
                  className="chat-restore-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsChatMinimized(false);
                  }}
                  title="확장"
                >
                  <Icon24 name="folding" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        autoClose={alertModal.type === 'success'}
        autoCloseDelay={2000}
      />
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        reason={upgradeModal.reason}
        onClose={() => {
          setUpgradeModal({ isOpen: false, reason: 'study' });
          if (projectSlug) {
            navigate(`/project/${projectSlug}`);
          } else {
            navigate('/b2b');
          }
        }}
      />
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ ...toast, isOpen: false })}
        autoClose={true}
        autoCloseDelay={2000}
      />
    </Layout>
  );
};

export default NewStudyPage;
